import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { NavItem } from '../../sidenav/navitem';
import { DefaultValuesService } from '../../../services/defaultvalues.service';
import { ComponentTool } from '../../../interfaces/componenttool';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { isClusterVisible } from '../../../config/cluster-visibility.config';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MarkdownModule } from 'ngx-markdown';
import { NgClass, NgStyle } from '@angular/common';

@Component({
    selector: 'landing-page-grid',
    standalone: true,
    imports: [MatIconModule, MatButtonModule, MatTooltipModule, MarkdownModule, NgClass, NgStyle],
    templateUrl: './landing-page-grid.component.html',
    styleUrls: ['./landing-page-grid.component.less', '../../../style/new-generic-styles.component.less']
})
export class LandingPageGridComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() items: NavItem[] = [];
    @Input() showClusterName: boolean = false;
    @Input() colorOffset: number = 0;
    @Input() clusterIdentifier: string = '';
    @Input() showHeader: boolean = false;
    @Input() showBackButton: boolean = false;
    @Input() showBackground: boolean = false;
    @Input() clusterName: string = '';
    @Input() isClusterView: boolean = false;
    @Output() itemClicked = new EventEmitter<NavItem>();

    elementRef: ElementRef | null | undefined;
    clusterIcon: string = 'widgets';
    componentToolEnum: typeof ComponentTool = ComponentTool;
    numberOfColumns: number = 3;

    @ViewChildren('itemRef') itemRefs!: QueryList<ElementRef>;
    uniformItemHeight: number | null = null;
    private heightTimer: any;

    constructor(
        @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
        private readonly router: Router,
        public defaults: DefaultValuesService,
        private readonly authService: MsalService
    ) {
        this.validateAuthentication();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.uniformItemHeight = null;
        this.updateGridCols();
        this.scheduleHeightCalculation();
    }
    
    updateGridCols() {
        const width = window.innerWidth;
        if (width >= 1920) {
            this.numberOfColumns = 4;
        } else if (width >= 1366) {
            this.numberOfColumns = 3;
        } else if (width >= 1100) {
            this.numberOfColumns = 2;
        } else {
            this.numberOfColumns = 1;
        }
    }

    private validateAuthentication(): void {
        const activeAccount = this.authService.instance.getActiveAccount();
        const allAccounts = this.authService.instance.getAllAccounts();
        
        if (!activeAccount && allAccounts.length === 0) {
            console.warn('No active authentication session found');
        }
    }

    ngAfterViewInit(): void {
        this.itemRefs.changes.subscribe(() => this.scheduleHeightCalculation());
        this.scheduleHeightCalculation();
    }

    private scheduleHeightCalculation(): void {
        clearTimeout(this.heightTimer);
        this.uniformItemHeight = null;
        this.heightTimer = setTimeout(() => {
            if (!this.itemRefs?.length) return;
            const max = Math.max(...this.itemRefs.map(el => (el.nativeElement as HTMLElement).offsetHeight));
            if (max > 0) this.uniformItemHeight = max;
        }, 100);
    }

    ngOnInit(): void {
        this.updateGridCols();
        // Disable outer scroll on #main-content and ensure height passes through to this component
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.style.overflow = 'hidden';
            // Set all intermediate containers to pass height through
            const containers = mainContent.querySelectorAll('.container-fluid, .main-container, .main-container > div');
            containers.forEach((el: Element) => {
                (el as HTMLElement).style.height = '100%';
            });
        }
        if (this.clusterIdentifier) {
            this.loadClusterData();
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.heightTimer);
        // Restore outer scroll and remove height overrides when leaving
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.style.overflow = '';
            const containers = mainContent.querySelectorAll('.container-fluid, .main-container, .main-container > div');
            containers.forEach((el: Element) => {
                (el as HTMLElement).style.height = '';
            });
        }
    }

    loadClusterData(): void {
        if (this.defaults.menuClusters) {
            const cluster = this.defaults.menuClusters.find(c => {
                const clusterRoute = (c.Route || '').replace(/^\//, '').toLowerCase();
                return clusterRoute === this.clusterIdentifier.toLowerCase();
            });
            
            if (cluster) {
                this.clusterName = cluster.DisplayName;
                this.clusterIcon = cluster.IconName || 'widgets';
                this.items = cluster.ClusterComponents?.map(module => ({
                    ...module,
                    ClusterName: cluster.DisplayName,
                    ClusterId: cluster.ClusterId
                })) || [];

                if (this.items.length > (this.numberOfColumns * 4)) {
                    setTimeout(() => {
                        const element = document.getElementById('landingpage-columns');
                        if (element) {
                            this.elementRef = new ElementRef<HTMLElement>(element);
                        }
                    });
                }
            }
        } else {
            setTimeout(() => this.loadClusterData(), 500);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items'] && this.items.length > (this.numberOfColumns * 4)) {
            setTimeout(() => {
                const element = document.getElementById('landingpage-columns');
                if (element) {
                    this.elementRef = new ElementRef<HTMLElement>(element);
                }
            });
        }
        if (changes['clusterIdentifier'] && this.clusterIdentifier && !this.items.length) {
            this.loadClusterData();
        }
    }

    @HostListener('wheel', ['$event'])
    onWheel(event: any) {
        // if (event.type === 'wheel' && this.elementRef) {
        //     event.preventDefault();
        //     this.elementRef.nativeElement.scrollLeft = this.elementRef.nativeElement.scrollLeft + event.deltaY;
        // }
    }

    onItemClick(item: NavItem): void {
        if (item.ClusterId !== undefined) {
            this.defaults.selectedItem = item.ClusterId;
            localStorage.setItem('currentSelectedItem', item.ClusterId.toString());
        }
        
        this.itemClicked.emit(item);
        
        if (this.isClusterView) {
            // Use the Route from backend data for Cluster Landing Pages
            if (item.Route) {
                const route = item.Route.startsWith('/') ? item.Route : '/' + item.Route;
                this.router.navigate([route]);
            }
            // If no Route, do nothing (sidenav will handle expansion)
        } else if (item.Route) {
            if (this.isExternalLink(item.Route)) {
                this.externalRedirect(item.Route);
            } else {
                this.router.navigate([item.Route]);
            }
        }
    }

    goBack(): void {
        this.router.navigate(['/']);
    }

    getItemClass(index: number, item: NavItem): string {
        const colorIndex = (index + this.colorOffset) % 3;
        
        if (colorIndex === 0) return 'tke-orange';
        if (colorIndex === 1) return 'tke-intermediate-color';
        if (colorIndex === 2) return 'tke-purple';
        
        return '';
    }

    isExternalLink(link: string): boolean {
        const httpRegex = /^http:\/\//i;
        const httpsRegex = /^https:\/\//i;
        return httpRegex.test(link) || httpsRegex.test(link);
    }

    externalRedirect(link: string): void {
        window.open(link, '_blank');
    }

    getDisplayTitle(item: NavItem): string {
        if (this.showClusterName && item.ClusterName) {
            return this.toTitleCase(`${item.ClusterName} - ${item.DisplayName}`);
        }
        return this.toTitleCase(item.DisplayName);
    }

    private toTitleCase(text: string): string {
        if (!text) return text;
        return text.split(' ').map(word => {
            // Preserve words that are already fully uppercase (e.g. "MERSY@CDP", "OSH")
            if (word === word.toUpperCase()) return word;
            // Capitalize first letter, lowercase the rest
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
    }

    isClusterVisible(item: any): boolean {
            const name = item.DisplayName || '';
            return isClusterVisible(this.defaults, name);
        }
}

